import { Option } from '@app/components/data-table/types/data-table';
import { Icons } from '@shared/components/content/icons';
import { SelectOption } from '@shared/components/form/select-form-field';

export const BUSINESS_TRAVEL_METHODOLOGY = {
  fuelUsage: 'fuel_usage',
  passengerTransport: 'passenger_transport',
  totalEmissions: 'total_emissions',
} as const;

export type BusinessTravelMethodology =
  (typeof BUSINESS_TRAVEL_METHODOLOGY)[keyof typeof BUSINESS_TRAVEL_METHODOLOGY];

export const METHODOLOGY_LABELS: Record<BusinessTravelMethodology, string> = {
  [BUSINESS_TRAVEL_METHODOLOGY.fuelUsage]: 'Fuel usage',
  [BUSINESS_TRAVEL_METHODOLOGY.passengerTransport]: 'Distance',
  [BUSINESS_TRAVEL_METHODOLOGY.totalEmissions]: 'Total emissions',
};

export const METHODOLOGY_DESCRIPTIONS: Record<
  BusinessTravelMethodology,
  string
> = {
  [BUSINESS_TRAVEL_METHODOLOGY.fuelUsage]:
    'Select a fuel and enter the amount purchased',
  [BUSINESS_TRAVEL_METHODOLOGY.passengerTransport]:
    'Select a transport mode and enter the distance travelled',
  [BUSINESS_TRAVEL_METHODOLOGY.totalEmissions]:
    'Enter a pre-calculated emissions value',
};

const DEFAULT_SECR_HELPER_TEXT =
  'Only select Yes if the travel was in an employee-owned or rented vehicle and the company reimbursed mileage claims. Excluded travel (e.g. employee commuting paid personally) should not be included.';

export const SECR_HELPER_TEXT: Record<BusinessTravelMethodology, string> = {
  [BUSINESS_TRAVEL_METHODOLOGY.fuelUsage]: DEFAULT_SECR_HELPER_TEXT,
  [BUSINESS_TRAVEL_METHODOLOGY.passengerTransport]: DEFAULT_SECR_HELPER_TEXT,
  [BUSINESS_TRAVEL_METHODOLOGY.totalEmissions]:
    'SECR needs underlying fuel or distance data, so it can’t be applied to pre-calculated totals.',
};

export const METHODOLOGY_FILTER_OPTIONS: Option[] = [
  {
    value: BUSINESS_TRAVEL_METHODOLOGY.fuelUsage,
    label: METHODOLOGY_LABELS[BUSINESS_TRAVEL_METHODOLOGY.fuelUsage],
  },
  {
    value: BUSINESS_TRAVEL_METHODOLOGY.passengerTransport,
    label: METHODOLOGY_LABELS[BUSINESS_TRAVEL_METHODOLOGY.passengerTransport],
  },
  {
    value: BUSINESS_TRAVEL_METHODOLOGY.totalEmissions,
    label: METHODOLOGY_LABELS[BUSINESS_TRAVEL_METHODOLOGY.totalEmissions],
  },
];

export const SECR_INCLUSION = {
  include: 'include',
  exclude: 'exclude',
  notApplicable: 'not_applicable',
} as const;

export type SecrInclusion =
  (typeof SECR_INCLUSION)[keyof typeof SECR_INCLUSION];

export const SECR_INCLUSION_OPTIONS: SelectOption[] = [
  {
    value: SECR_INCLUSION.include,
    label: 'Include in SECR',
  },
  {
    value: SECR_INCLUSION.exclude,
    label: 'Omit from SECR',
  },
  {
    value: SECR_INCLUSION.notApplicable,
    label: 'Not applicable',
    hidden: true,
  },
];

export const METHODOLOGY_OPTIONS: SelectOption[] = [
  {
    value: BUSINESS_TRAVEL_METHODOLOGY.fuelUsage,
    label: METHODOLOGY_LABELS[BUSINESS_TRAVEL_METHODOLOGY.fuelUsage],
    description:
      METHODOLOGY_DESCRIPTIONS[BUSINESS_TRAVEL_METHODOLOGY.fuelUsage],
    icon: <Icons.fuel className="size-4" />,
  },
  {
    value: BUSINESS_TRAVEL_METHODOLOGY.passengerTransport,
    label: METHODOLOGY_LABELS[BUSINESS_TRAVEL_METHODOLOGY.passengerTransport],
    description:
      METHODOLOGY_DESCRIPTIONS[BUSINESS_TRAVEL_METHODOLOGY.passengerTransport],
    icon: <Icons.car className="size-4" />,
  },
  {
    value: BUSINESS_TRAVEL_METHODOLOGY.totalEmissions,
    label: METHODOLOGY_LABELS[BUSINESS_TRAVEL_METHODOLOGY.totalEmissions],
    description:
      METHODOLOGY_DESCRIPTIONS[BUSINESS_TRAVEL_METHODOLOGY.totalEmissions],
    icon: <Icons.calculator className="size-4" />,
  },
];
